import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from "prop-types";
import { titleCase } from '../../utils/helpers';
import { getOcsPrescriptionItems } from '../../utils/ocs-generator';

const mealLabel = (meal) => (meal ? meal.charAt(0) + meal.slice(1).toLowerCase().replace('_', ' ') : '');

// Every timing of every item that has timings, all ticked.
const selectAll = (items) =>
    Object.fromEntries(items.map(({ key, item }) => [key, item.time.map((_, i) => i)]));

const OCSCreateModal = ({ handleGenerateOcs, orderData }) => {
    const [patientName, setPatientName] = useState()

    const items = useMemo(
        () => getOcsPrescriptionItems(orderData).filter(({ item }) => item?.time?.length > 0),
        [orderData]
    );
    const [selection, setSelection] = useState(() => selectAll(items));

    useEffect(() => {
        setPatientName(titleCase(orderData?.mapUserPatient?.Patient?.name))
    }, [orderData])

    useEffect(() => {
        setSelection(selectAll(items));
    }, [items])

    const isChecked = (key, index) => (selection[key] || []).includes(index);

    const toggleTiming = (key, index) =>
        setSelection((prev) => {
            const current = prev[key] || [];
            const next = current.includes(index) ? current.filter((i) => i !== index) : [...current, index].sort((a, b) => a - b);
            return { ...prev, [key]: next };
        });

    const toggleItem = (key, count) =>
        setSelection((prev) => ({
            ...prev,
            [key]: (prev[key] || []).length === count ? [] : Array.from({ length: count }, (_, i) => i),
        }));

    const selectedCount = Object.values(selection).reduce((total, picked) => total + picked.length, 0);
    const totalCount = items.reduce((total, { item }) => total + item.time.length, 0);

    return (
        <div className='flex flex-col gap-[0.9rem]' >
            <div className='flex justify-between' >
                <span className='text-[1.5rem] font-bold' >Create OCS</span>
            </div>
            <div className='flex flex-col gap-[0.9rem]' >
                <span className='text-[1.2rem]' >Order ID: <span className='text-[1.2rem] font-semibold' >{`#${orderData.id}`}</span></span>
            </div>
            <div className='flex flex-col gap-[0.9rem]' >
                <input type="text" className='p-4 focus:outline-none placeholder:opacity-50  h-10 border-gray-200 border w-full' placeholder='Patient Name' value={patientName} onChange={(e) => setPatientName(e.target.value)} />
            </div>
            <div className='flex flex-col gap-3' >
                <div className='flex justify-between items-center' >
                    <span className='text-[1.1rem] font-semibold' >Medicines & timings</span>
                    <div className='flex items-center gap-3' >
                        <span className='text-sm text-gray-500 tabular-nums' >{selectedCount} of {totalCount} selected</span>
                        <button type="button" className='text-sm font-semibold text-[#158D7B] hover:underline' onClick={() => setSelection(selectedCount === totalCount ? {} : selectAll(items))} >
                            {selectedCount === totalCount ? 'Clear all' : 'Select all'}
                        </button>
                    </div>
                </div>
                <div className='flex flex-col gap-2.5 max-h-[40vh] overflow-y-auto pr-1' >
                    {items.map(({ key, item }) => {
                        const picked = (selection[key] || []).length;
                        const allPicked = picked === item.time.length;
                        return (
                            <div key={key} className={`rounded-xl border p-3 transition-colors ${picked ? 'border-[#158D7B]/40 bg-[#158D7B]/[0.04]' : 'border-gray-200 bg-white-50'}`} >
                                <button type="button" className='flex w-full items-center gap-3 text-left' onClick={() => toggleItem(key, item.time.length)} >
                                    <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border text-xs font-bold leading-none text-white-50 transition-colors ${picked ? 'border-[#158D7B] bg-[#158D7B]' : 'border-gray-300 bg-white-50'}`} >
                                        {allPicked ? '✓' : picked ? '–' : ''}
                                    </span>
                                    <span className='flex-1 font-semibold text-gray-900' >{item?.item?.name || `Item ${key + 1}`}</span>
                                    <span className='text-xs text-gray-500 tabular-nums' >{picked}/{item.time.length}</span>
                                </button>
                                <div className='mt-2.5 flex flex-wrap gap-2 pl-8' >
                                    {item.time.map((time, i) => {
                                        const on = isChecked(key, i);
                                        return (
                                            <button
                                                key={i}
                                                type="button"
                                                aria-pressed={on}
                                                onClick={() => toggleTiming(key, i)}
                                                className={`rounded-full border px-3 py-1 text-sm font-medium tabular-nums transition-colors ${on ? 'border-[#158D7B] bg-[#158D7B] text-white-50' : 'border-gray-300 bg-white-50 text-gray-500 hover:border-[#158D7B] hover:text-[#158D7B]'}`}
                                            >
                                                {time}{item.meal?.[i] ? ` · ${mealLabel(item.meal[i])}` : ''}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
            <div className='flex flex-col gap-[0.9rem]' >
                <button disabled={!selectedCount} className='bg-[#158D7B] text-white p-4 rounded-md text-white-50 disabled:opacity-50 disabled:cursor-not-allowed' onClick={() => handleGenerateOcs(orderData, patientName, selection)} >
                    {selectedCount === totalCount ? 'Generate OCS' : 'Generate partial OCS'}
                </button>
            </div>
        </div >
    )
}

OCSCreateModal.propTypes = {
    handleGenerateOcs: PropTypes.func.isRequired,
    orderData: PropTypes.object.isRequired,
};

export default OCSCreateModal
